import { CernerRole, Responsibility, UserRole } from '@lmc/ts-common'
import { getNominative } from 'common/utils'
import { isGroupUser as checkIsGroupUser } from 'utils'

// ---------------------------------------------------------------------------
// Role options — building the options/notes for the Role select field
// ---------------------------------------------------------------------------

const ROLE_OPTIONS_CAREHOME_BASE = [
  { label: 'Carer', value: UserRole.carer },
  { label: 'Senior Carer', value: UserRole.seniorCarer }
]

const MANAGEMENT_ROLE = { label: 'Management', value: UserRole.management }
const ADMIN_ROLE = { label: 'Admin', value: UserRole.careHomeAdmin }
const AUDIT_VIEW_ROLE = { label: 'Audit View', value: UserRole.auditView }
const IT_SUPPORT_ROLE = { label: 'IT', value: UserRole.it }

const ROLE_OPTIONS_GROUP = [
  { label: 'Group Admin', value: UserRole.groupAdmin },
  { label: 'Group Manager', value: UserRole.groupManager }
]

const ROLE_NOTE_CAREHOME = (preferredPeopleNominative: string) =>
  `Carers can only access the Carer App, while Senior Carers also have limited access to the Care Office (viewing ${getNominative(
    preferredPeopleNominative || '',
    'Resident'
  )} and Report information only). Admins have full access to the Care Office.`
const ROLE_NOTE_GROUP =
  'Group Admins can fully access the group dashboard. Group Managers will not be able to edit any information.'

export const getRoleFieldsProps = (
  initial?: Record<string, unknown> | undefined,
  role?: UserRole,
  preferredPeopleNominative = '',
  managementRoleEnabled = false,
  auditViewRoleEnabled = false,
  itSupportRoleEnabled = false
) => {
  const isGroupUser = checkIsGroupUser(initial?.role as string | undefined)

  const careHomeOptions = [...ROLE_OPTIONS_CAREHOME_BASE]
  if (managementRoleEnabled) {
    careHomeOptions.push(MANAGEMENT_ROLE)
  }
  careHomeOptions.push(ADMIN_ROLE)
  if (auditViewRoleEnabled) {
    careHomeOptions.push(AUDIT_VIEW_ROLE)
  }

  if (itSupportRoleEnabled) {
    careHomeOptions.push(IT_SUPPORT_ROLE)
  }

  const viewerIsManagement = role === UserRole.management
  const targetIsAdmin = initial?.role === UserRole.careHomeAdmin

  // Management viewers: admin option is disabled (can't promote to admin).
  // If the target IS already an admin, management can't demote them either —
  // the entire field is locked (roleFieldDisabled) so all options are inert.
  const visibleCareHomeOptions = viewerIsManagement
    ? careHomeOptions.map((o) =>
        o.value === UserRole.careHomeAdmin ? { ...o, disabled: true } : o
      )
    : careHomeOptions

  const roleFieldDisabled = viewerIsManagement && targetIsAdmin

  const ROLE_OPTIONS = isGroupUser ? ROLE_OPTIONS_GROUP : visibleCareHomeOptions

  const ROLE_NOTE = isGroupUser
    ? ROLE_NOTE_GROUP
    : ROLE_NOTE_CAREHOME(preferredPeopleNominative)

  return { ROLE_NOTE, ROLE_OPTIONS, roleFieldDisabled, isGroupUser }
}

// ---------------------------------------------------------------------------
// Permission rules — which capabilities a viewer/target role may hold
// ---------------------------------------------------------------------------

// Returns false when the viewer should not be permitted to make destructive
// edits (email, status, site access) to the target — i.e. the target holds a
// role with higher privilege than the viewer. Add new restricted pairs here as
// additional roles are introduced.
export const canViewerEditAccessAndPermissionsOnTarget = (
  viewerRole?: UserRole,
  targetRole?: UserRole
): boolean => {
  if (
    viewerRole === UserRole.management &&
    targetRole === UserRole.careHomeAdmin
  ) {
    return false
  }
  return true
}

// Which permission fields a given *target* role is allowed to hold in the
// team-member edit form. Audit View and IT are oversight/administrative roles
// with no care-giving remit, so they can't hold medication; safeguarding and
// care management are likewise limited to the roles that need them. Only the
// roles selectable in this form are listed; any unlisted role falls back to
// DEFAULT_ROLE_CAPABILITIES (medication only), preserving the prior behaviour
// where every role except the oversight ones could hold medication. Viewer-based
// restrictions (e.g. management editing an admin) are handled separately — see
// canViewerEditAccessAndPermissionsOnTarget.
interface RoleCapabilities {
  careManagement: boolean
  medication: boolean
  safeguarding: boolean
}

const DEFAULT_ROLE_CAPABILITIES: RoleCapabilities = {
  careManagement: false,
  medication: true,
  safeguarding: false
}

const ROLE_CAPABILITIES: Partial<Record<UserRole, RoleCapabilities>> = {
  [UserRole.carer]: {
    careManagement: false,
    medication: true,
    safeguarding: false
  },
  [UserRole.seniorCarer]: {
    careManagement: true,
    medication: true,
    safeguarding: false
  },
  [UserRole.management]: {
    careManagement: false,
    medication: true,
    safeguarding: true
  },
  [UserRole.careHomeAdmin]: {
    careManagement: false,
    medication: true,
    safeguarding: true
  },
  [UserRole.auditView]: {
    careManagement: false,
    medication: false,
    safeguarding: false
  },
  [UserRole.it]: {
    careManagement: false,
    medication: false,
    safeguarding: false
  }
}

export const getRoleCapabilities = (role?: UserRole): RoleCapabilities =>
  (role && ROLE_CAPABILITIES[role]) || DEFAULT_ROLE_CAPABILITIES

// ---------------------------------------------------------------------------
// Field options & helpers — option lists and value getters/setters for the
// other permission fields (care management, Cerner, safeguarding)
// ---------------------------------------------------------------------------

export const CARE_MANAGEMENT_LABEL = 'Care Management'

export const CERNER_ROLE_OPTIONS = [
  { value: CernerRole.NoAccess, label: 'No Access' },
  { value: CernerRole.SeniorCarer, label: 'Senior Carer Access' },
  { value: CernerRole.Admin, label: 'Admin Access' },
  { value: CernerRole.RegisteredManager, label: 'Registered Manager Access' }
]

export const getPermissionBooleanByTemplate = (
  label?: string,
  templates?: { id: string; label: string }[]
) => templates?.find((item) => item.label === label)

export const SAFEGUARDING_RADIO_OPTIONS = [
  { value: Responsibility.SafeguardingOfficer, label: 'Yes' },
  { value: null, label: 'No' }
] as const

export const getSafeguardingPermissionValue = (
  permissions: string[] = []
): string | null => {
  return permissions.includes(Responsibility.SafeguardingOfficer)
    ? Responsibility.SafeguardingOfficer
    : null
}

export const updatePermissionsForSafeguarding = (
  currentPermissions: string[] = [],
  shouldHavePermission: boolean
): string[] => {
  if (shouldHavePermission) {
    return currentPermissions.includes(Responsibility.SafeguardingOfficer)
      ? currentPermissions
      : [...currentPermissions, Responsibility.SafeguardingOfficer]
  }

  return currentPermissions.filter(
    (permission) => permission !== Responsibility.SafeguardingOfficer
  )
}
